import { useCallback, useEffect, useRef, useState } from "react";
import { fetchHotWalletPolicyProposals, type ApiErrorInfo, type ApiHotWalletPolicyProposal } from "@/lib/api";

interface UseHotWalletPolicyProposalsResult {
  proposals: ApiHotWalletPolicyProposal[];
  loading: boolean;
  error: ApiErrorInfo | null;
  refresh: () => void;
}

/** List hook for the policy-proposals section of HW-S5 (developer & policy
 * console). Webhooks and API keys on that same page are the existing, real
 * developer-webhooks / developer-api-keys features — this hook is only for
 * the new hot-wallets policy-change panel. */
export function useHotWalletPolicyProposals(): UseHotWalletPolicyProposalsResult {
  const [proposals, setProposals] = useState<ApiHotWalletPolicyProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiErrorInfo | null>(null);
  // Only the most recent load may write state. The retry button calls `refresh`
  // while the initial call may still be in flight; without this, the slower
  // earlier response lands last and a successful retry is overwritten by the
  // failure it replaced (or by rows the user just refreshed away) — an
  // out-of-order write, so nothing else catches it. Mirrors `use-hot-wallet-deposits`.
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchHotWalletPolicyProposals();
      if (seq !== loadSeq.current) return;
      if (result.error) {
        setProposals([]);
        setError(result.error);
      } else {
        setProposals(result.items);
      }
    } catch {
      if (seq !== loadSeq.current) return;
      setProposals([]);
      setError({ reasonCode: "NETWORK_ERROR" });
    } finally {
      // A superseded load must not clear the spinner the current one owns.
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { proposals, loading, error, refresh: load };
}
